/**
 * The audio mixer — the thing `state/audio-settings.ts` was written against.
 *
 * Until now those three sliders scaled nothing, and the module said so in its
 * own header. This is the piece that makes them real: every sound in the game
 * goes through exactly one of the two channel gain nodes built here, and both
 * are driven from `effectiveVolume(...)` rather than from any level maths
 * repeated locally.
 *
 * ─── Why there is no master gain node ───────────────────────────────────────
 * The obvious graph is source -> channel -> master -> destination. It is
 * wrong here, because `effectiveVolume(settings, 'music')` ALREADY returns
 * `master.level * music.level` (and 0 if either is muted). A master node on
 * top of that would square the master slider — at 0.7 the player would hear
 * 0.49. One node per channel, master folded in by the function that owns the
 * rule, is the only arrangement that can't drift from the settings UI.
 *
 * ─── Why the context starts suspended ───────────────────────────────────────
 * Browsers refuse to start an AudioContext outside a user gesture. Creating it
 * eagerly and resuming on the first pointer/key event means the graph, the
 * decodes and the fetches can all happen during load, and the first gesture
 * costs a `resume()` rather than a cold start plus a network round trip.
 *
 * ─── Why decoded buffers get their silence measured ─────────────────────────
 * These loops are seam-verified as *files* — the last sample and the first
 * sample are originally-adjacent, so the wrap is sample-continuous. A decoder
 * can still break that: AAC carries priming samples, and Chrome has
 * historically handed back a buffer with that lead-in still on the front. A
 * loop over such a buffer plays a short silence every lap, which is exactly
 * the click the file-level work was done to avoid. So rather than trust any
 * decoder, `measureSilence` finds the leading and trailing runs of digital
 * silence in the decoded buffer and the loop is set to play between them.
 * Ogg/Opus carries a pre-skip field that every decoder honours and measures
 * as zero lead-in, so this costs nothing on the primary format and rescues
 * the fallback one.
 */
import {
  audioSettingsStore, effectiveVolume,
  type AudioChannelId, type AudioSettings,
} from '../state/audio-settings';

/** The two channels a sound can be routed to. `master` is not a bus — see the header. */
export type MixerChannel = Exclude<AudioChannelId, 'master'>;

/** A decoded, silence-measured clip ready to be looped. */
interface LoadedClip {
  buffer: AudioBuffer;
  /** Seconds of digital silence the decoder put on the front. Usually 0. */
  leadIn: number;
  /** Where the musical content ends, seconds. `buffer.duration` minus any trailing pad. */
  endsAt: number;
}

export interface MixerHandle {
  /** The shared context. Exposed so emitters can build their own nodes on it. */
  readonly ctx: AudioContext;
  /** Destination for a given channel — connect emitter chains here, not to `ctx.destination`. */
  busFor(channel: MixerChannel): GainNode;
  /** Fetch + decode + measure, cached per URL. Safe to call repeatedly. */
  load(url: string): Promise<LoadedClip>;
  /**
   * Start a looping clip on a channel. Returns a stop function that fades out
   * and releases the nodes. `gain` is the clip's own level within its channel,
   * NOT a replacement for the player's slider.
   */
  playLoop(url: string, channel: MixerChannel, gain?: number, fadeSeconds?: number): Promise<() => void>;
  /**
   * Play a clip once and forget it. For stingers — a level-up, a pickup — as
   * opposed to the beds `playLoop` handles. Resolves when playback has been
   * scheduled, not when it finishes.
   */
  playOnce(url: string, channel: MixerChannel, gain?: number): Promise<void>;
  /** True once the context is actually running (i.e. a gesture has unlocked it). */
  isRunning(): boolean;
  dispose(): void;
}

/**
 * Which encoding this browser should be served.
 *
 * Opus is the primary: smaller at equal quality for this material, and its
 * pre-skip field makes the decoded length exact. Safari did not decode Ogg
 * Opus before 17, so AAC ships alongside as the fallback rather than as a
 * second-class path — both files are produced from the same seam-verified
 * master.
 */
export function preferredExtension(probe?: HTMLAudioElement): '.ogg' | '.m4a' {
  try {
    const el = probe ?? new Audio();
    // `canPlayType` returns '', 'maybe' or 'probably'. Anything non-empty is
    // a yes; browsers are deliberately non-committal about codecs they can
    // decode but not guarantee for every bitrate.
    if (el.canPlayType('audio/ogg; codecs="opus"')) return '.ogg';
  } catch {
    // No Audio constructor at all (a non-DOM test environment). AAC is the
    // safer guess for anything that later turns out to be a real browser.
  }
  return '.m4a';
}

/**
 * Leading and trailing runs of exactly-zero samples in a decoded buffer.
 *
 * Exactly zero, not "below a threshold": codec padding is literal digital
 * silence, whereas a quiet musical fade is not, and trimming a real fade
 * would shorten the loop and reintroduce the seam. Only channel 0 is scanned
 * — padding is applied to every channel identically, and a stereo scan would
 * double the work to reach the same answer.
 */
export function measureSilence(buffer: AudioBuffer): { leadIn: number; endsAt: number } {
  const data = buffer.getChannelData(0);
  const n = data.length;
  let head = 0;
  while (head < n && data[head] === 0) head += 1;
  // A buffer that is silent end to end would leave `head === n` and produce a
  // negative-length loop below. Treat it as untrimmed and let it play silently.
  if (head === n) return { leadIn: 0, endsAt: buffer.duration };
  let tail = n - 1;
  while (tail > head && data[tail] === 0) tail -= 1;
  return {
    leadIn: head / buffer.sampleRate,
    endsAt: (tail + 1) / buffer.sampleRate,
  };
}

export function createMixer(): MixerHandle {
  const Ctor: typeof AudioContext | undefined =
    (globalThis as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
      .AudioContext
    ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) throw new Error('No Web Audio support in this environment');

  const ctx = new Ctor();
  const buses: Record<MixerChannel, GainNode> = {
    music: ctx.createGain(),
    effects: ctx.createGain(),
  };
  for (const bus of Object.values(buses)) bus.connect(ctx.destination);

  const applySettings = (s: AudioSettings): void => {
    // `setTargetAtTime` rather than a bare assignment: dragging a slider
    // fires a stream of updates, and stepping a gain discontinuously on each
    // one is an audible zipper. 15ms is short enough to feel instant.
    for (const id of Object.keys(buses) as MixerChannel[]) {
      buses[id].gain.setTargetAtTime(effectiveVolume(s, id), ctx.currentTime, 0.015);
    }
  };
  const unsubscribe = audioSettingsStore.subscribe(applySettings);

  // ── Unlock ────────────────────────────────────────────────────────────────
  // Autoplay policy: a context created outside a gesture starts 'suspended'
  // and stays there until resumed from within one. These listeners are
  // `once`-style — the first gesture of any kind is enough, and they remove
  // themselves so the game does not carry three listeners for its lifetime.
  const unlock = (): void => {
    if (ctx.state === 'suspended') void ctx.resume();
    removeUnlockListeners();
  };
  const unlockEvents: (keyof DocumentEventMap)[] = ['pointerdown', 'keydown', 'touchstart'];
  const removeUnlockListeners = (): void => {
    for (const e of unlockEvents) globalThis.removeEventListener?.(e, unlock);
  };
  for (const e of unlockEvents) globalThis.addEventListener?.(e, unlock, { passive: true });

  let disposed = false;
  const ext = preferredExtension();
  const cache = new Map<string, Promise<LoadedClip>>();

  const load = (url: string): Promise<LoadedClip> => {
    // The manifest stores extension-less keys so one entry serves both
    // encodings; the browser's own answer picks the file.
    const resolved = /\.(ogg|m4a)$/.test(url) ? url : url + ext;
    const hit = cache.get(resolved);
    if (hit) return hit;
    const p = fetch(resolved)
      .then((r) => {
        if (!r.ok) throw new Error(`audio ${r.status} for ${resolved}`);
        return r.arrayBuffer();
      })
      .then((bytes) => ctx.decodeAudioData(bytes))
      .then((buffer) => ({ buffer, ...measureSilence(buffer) }));
    cache.set(resolved, p);
    // A failed fetch must not poison the cache forever — a later retry should
    // be allowed to succeed. The rejection is still returned to this caller.
    p.catch(() => cache.delete(resolved));
    return p;
  };

  const playLoop = async (
    url: string, channel: MixerChannel, gain = 1, fadeSeconds = 1.5,
  ): Promise<() => void> => {
    const clip = await load(url);
    const src = ctx.createBufferSource();
    src.buffer = clip.buffer;
    src.loop = true;
    // The measured window, not the whole buffer — see the header.
    src.loopStart = clip.leadIn;
    src.loopEnd = clip.endsAt;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, ctx.currentTime);
    g.gain.linearRampToValueAtTime(gain, ctx.currentTime + fadeSeconds);
    src.connect(g).connect(buses[channel]);
    // Offset the start too, or the first lap plays the padding the loop
    // window was set up to skip.
    src.start(0, clip.leadIn);

    let stopped = false;
    return () => {
      if (stopped) return;
      stopped = true;
      const t = ctx.currentTime;
      g.gain.cancelScheduledValues(t);
      g.gain.setValueAtTime(g.gain.value, t);
      g.gain.linearRampToValueAtTime(0, t + fadeSeconds);
      // Stop after the fade, not on it, or the ramp is cut off by the silence
      // it was scheduling towards.
      src.stop(t + fadeSeconds + 0.05);
      src.onended = () => { src.disconnect(); g.disconnect(); };
    };
  };

  const playOnce = async (url: string, channel: MixerChannel, gain = 1): Promise<void> => {
    const clip = await load(url);
    const src = ctx.createBufferSource();
    src.buffer = clip.buffer;
    // No loop, and therefore no loop window — but still start past any
    // decoder lead-in, or a stinger fires late by the length of the padding.
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(g).connect(buses[channel]);
    src.start(0, clip.leadIn);
    // Released on its own end rather than tracked: a one-shot has no handle
    // anyone needs, and leaving the nodes connected would leak one pair per
    // level-up for the life of the session.
    src.onended = () => { src.disconnect(); g.disconnect(); };
  };

  return {
    ctx,
    busFor: (channel) => buses[channel],
    load,
    playLoop,
    playOnce,
    isRunning: () => ctx.state === 'running',
    dispose: () => {
      // Idempotent, and it has to be: `startBaseView`'s teardown runs on every
      // SPA remount, and calling `close()` on an already-closed context throws
      // `InvalidStateError` as an UNCAUGHT page error — which is how this was
      // found, in the browser rather than in a test. Both guards matter: the
      // flag stops a second dispose from this module, and the state check
      // stops one from a context closed out from under us.
      if (disposed) return;
      disposed = true;
      unsubscribe();
      removeUnlockListeners();
      for (const bus of Object.values(buses)) bus.disconnect();
      if (ctx.state !== 'closed') void ctx.close().catch(() => { /* already going */ });
    },
  };
}
